const MAX_REASON_TEXT_LENGTH = 500;
const MAX_REASON_IN_MESSAGE_LENGTH = 100;

export interface RejectionReport {
    message: string;
    /** always a string - an object would reach Error Reporting as "[object Object]" */
    additional?: string;
}

function toJson(reason: object): string | undefined {
    try {
        return JSON.stringify(reason)?.slice(0, MAX_REASON_TEXT_LENGTH);
    } catch {
        // circular structure
        return undefined;
    }
}

/** String(reason), unless that is only the default "[object Object]" and says nothing. */
function ownText(reason: object): string | undefined {
    try {
        const text = String(reason);
        return /^\[object [^\]]*\]$/.test(text) ? undefined : text;
    } catch {
        // no toString at all (Object.create(null))
        return undefined;
    }
}

/**
 * What an unhandled promise rejection is reported as. The stack goes along when there is one -
 * the log filter needs the frames to recognise a rejection raised by a browser extension.
 * Safari DOMExceptions and errors from extensions often have none; then the name of the error
 * or the serialised reason stands in. That detail starts with a line break because the server
 * appends a string directly to the message.
 */
export function describeRejectionReason(reason: unknown): RejectionReport {
    if (typeof reason !== 'object' || reason === null) {
        return { message: String(reason) };
    }
    const { message, stack, code } = reason as { message?: unknown, stack?: unknown, code?: unknown };
    const json = toJson(reason);
    let reportMessage: string;
    if (reason instanceof Error) {
        reportMessage = reason.message;
    } else {
        // an error from another realm (extension, iframe) fails instanceof but still prints as "TypeError: …"
        reportMessage = ownText(reason)
            ?? (typeof message === 'string' ? message : (json ?? '(unserializable object)').slice(0, MAX_REASON_IN_MESSAGE_LENGTH));
    }
    if (typeof stack === 'string' && stack !== '') {
        return { message: reportMessage, additional: stack };
    }
    if (reason instanceof Error) {
        return { message: reportMessage, additional: '\n' + reason.name + (code != null ? ` (code ${code})` : '') };
    }
    // "{}" is all JSON sees of an error from another realm
    return json === undefined || json === '{}' ? { message: reportMessage } : { message: reportMessage, additional: '\n' + json };
}
